"use client"

import { useEffect, useMemo, useRef, useState } from 'react'
import GradientText from './GradientText'
import { trackEvent } from '../utils/analytics'

export default function Hero() {
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    // Detect mobile on mount
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()

    const handleScroll = () => {
      const section = sectionRef.current
      if (!section) return
      
      // Get the parent container (the 200vh wrapper)
      const container = section.parentElement
      if (!container) return
      
      const containerRect = container.getBoundingClientRect()
      const viewportH = window.innerHeight
      
      // Calculate progress based on how far through the container we've scrolled
      // When container top is at viewport top (0), progress = 0
      // When container top is at -100vh, progress = 1
      const scrolled = Math.max(0, -containerRect.top)
      const totalScrollable = viewportH // We want full progress after 1 viewport height
      const calculatedProgress = Math.min(scrolled / totalScrollable, 1)
      
      setProgress(calculatedProgress)
    }

    const handleResize = () => {
      checkMobile()
      handleScroll()
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const frameStyle = useMemo(() => {
    // Dynamic z-index: Start at 20 and decrease as scroll progresses
    // At progress 0 (initial): z-index = 20 (behind features at z-index 30)
    // At progress 1: z-index = 1 (fully behind features)
    const dynamicZIndex = Math.max(1, 20 - Math.floor(progress * 19))
    
    // Simplified transforms for mobile, full transforms for desktop
    if (isMobile) {
      const scale = 1 - progress * 0.03 // Reduce scale effect on mobile
      const translateY = progress * -10  // Reduce movement on mobile
      
      return {
        transform: `translate3d(0, ${translateY}px, 0) scale(${scale})`,
        zIndex: dynamicZIndex,
      } as React.CSSProperties
    } else {
      const scale = 1 - progress * 0.05
      const translateY = progress * -20
      const translateZ = progress * -200
      const rotateX = progress * 15
      
      return {
        transform: `translate3d(0, ${translateY}px, ${translateZ}px) scale(${scale}) rotateX(${rotateX}deg)`,
        transformStyle: 'preserve-3d' as any,
        WebkitTransform: `translate3d(0, ${translateY}px, ${translateZ}px) scale(${scale}) rotateX(${rotateX}deg)`,
        WebkitTransformStyle: 'preserve-3d' as any,
        zIndex: dynamicZIndex,
      } as React.CSSProperties
    }
  }, [progress, isMobile])

  return (
    <section ref={sectionRef} className={`relative h-screen p-2 sm:p-4 pb-8 sm:pb-12 bg-gray-50 ${isMobile ? '' : 'sticky top-0'}`}>
      {/* Main Frame Container */}
      <div
        className="left-2 right-2 sm:left-4 sm:right-4 h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)] max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] mx-auto bg-gradient-to-b from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden will-change-transform hero-frame"
        style={frameStyle}
      >
        
        {/* Mountain Background Image - Optimized for LCP */}
        <div className="absolute inset-0">
          <img
            src="/images/hero_background_monet.webp"
            alt=""
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center' }}
          />
          {/* Darkening overlay from bottom to top */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-4 sm:px-8">
          <div className="flex flex-col items-center justify-center text-center">
          {/* Badge */}
          <div className="glassmorphism-button group inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 text-white text-xs font-medium mb-6 sm:mb-8 animate-fade-in cursor-default">
            <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            Now in Beta
          </div>
          
          {/* Headline - Mobile optimized */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-light mb-4 sm:mb-6 animate-slide-up text-balance text-center max-w-5xl text-white leading-tight drop-shadow-lg tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 3.75rem)' }}>
            Don't Just Launch a Business.
            <span className="block mt-2 sm:mt-3 mb-2 font-normal" style={{ paddingBottom: '8px' }}>
              <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]} animationSpeed={8}>
                Launch a Winning Strategy.
              </GradientText>
            </span>
          </h1>
          
          {/* Subheading - Mobile optimized */}
          <p className="text-sm sm:text-base md:text-lg font-light mb-8 sm:mb-12 max-w-3xl mx-auto animate-slide-up animation-delay-200 text-center text-blue-100/95 leading-relaxed drop-shadow-sm px-2 sm:px-0">
            Stop guessing. VentureCompass is your dedicated AI CMO, generating a complete go-to-market blueprint, from market analysis to ad campaigns, in minutes.
          </p>
          
          {/* Glassmorphic Pill Button */}
          <div className="flex justify-center items-center mb-12 sm:mb-16 animate-slide-up animation-delay-400">
            <a
              href="https://app.venturecompass.ai/signup"
              className="glassmorphism-button group inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 text-white font-normal text-base sm:text-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2"
              onClick={() => trackEvent('cta_click', { location: 'hero', text: 'Find Your Winning Strategy' })}
            >
              <span className="hidden sm:inline">Find Your Winning Strategy</span>
              <span className="sm:hidden">Get Your Blueprint</span>
              <svg className="ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>
          
          </div>
        </div>

      </div>
    </section>
  )
}